import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

export default function VideoListModal({ isOpen, project, onClose, onSelectVideo }) {
  if (!isOpen || !project) return null

  const videos = project.videos || []

  const [computedDurations, setComputedDurations] = useState({})
  const [computedThumbnails, setComputedThumbnails] = useState({})

  const getYouTubeId = (url) => {
    if (!url) return ''
    try {
      const u = String(url)
      if (u.includes('youtube.com/watch?v=')) return u.split('v=')[1]?.split('&')[0]
      if (u.includes('youtu.be/')) return u.split('youtu.be/')[1]?.split('?')[0]
      if (u.includes('youtube.com/embed/')) return u.split('embed/')[1]?.split('?')[0]
    } catch {}
    return ''
  }

  const getDerivedThumbnail = (video) => {
    const src = video?.thumbnail ? '' : (video?.url || video?.src || video?.link || '')
    if (!src) return ''
    // YouTube
    const ytId = getYouTubeId(src)
    if (ytId) return `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`
    // Cloudinary heuristic: replace extension with .jpg
    if (src.includes('res.cloudinary.com')) {
      try {
        const urlObj = new URL(src, window.location.origin)
        const pathname = urlObj.pathname
        const withoutExt = pathname.replace(/\.[a-zA-Z0-9]+$/, '')
        return `${urlObj.origin}${withoutExt}.jpg`
      } catch {
        return src.replace(/\.[a-zA-Z0-9]+$/, '.jpg')
      }
    }
    // Generic: try appending #t=1 to let browser pick a frame (not reliable for <img>)
    return ''
  }

  // Precompute thumbnails for items missing thumbnails
  useEffect(() => {
    const updates = {}
    videos.forEach((v, idx) => {
      if (!v?.thumbnail) {
        const derived = getDerivedThumbnail(v)
        if (derived) updates[idx] = derived
      }
    })
    if (Object.keys(updates).length > 0) {
      setComputedThumbnails((prev) => ({ ...prev, ...updates }))
    }
  }, [videos])

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds)) return ''
    const total = Math.max(0, Math.floor(seconds))
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins}:${String(secs).padStart(2, '0')}`
  }

  useEffect(() => {
    let isCancelled = false
    const controllers = []

    const computeMissingDurations = async () => {
      const updates = {}
      await Promise.all(
        videos.map((v, idx) => {
          if (v?.duration || !(v?.url || v?.src || v?.link)) return null
          return new Promise((resolve) => {
            const videoEl = document.createElement('video')
            const src = v.url || v.src || v.link
            videoEl.preload = 'metadata'
            videoEl.src = src
            const cleanup = () => {
              videoEl.removeAttribute('src')
              videoEl.load()
            }
            videoEl.onloadedmetadata = () => {
              if (!isCancelled) {
                updates[idx] = formatDuration(videoEl.duration)
              }
              cleanup()
              resolve()
            }
            videoEl.onerror = () => {
              cleanup()
              resolve()
            }
          })
        })
      )
      if (!isCancelled && Object.keys(updates).length > 0) {
        setComputedDurations((prev) => ({ ...prev, ...updates }))
      }
    }

    computeMissingDurations()
    return () => {
      isCancelled = true
      controllers.forEach((c) => c.abort && c.abort())
    }
  }, [videos])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-4xl mx-4 bg-black border border-white/15 rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-6 py-5 border-b border-white/10">
            <div className="min-w-0 pr-4">
              <h3 className="text-white text-xl sm:text-2xl font-bold leading-tight truncate">{project.title}</h3>
              <div className="mt-2 space-y-1.5 text-[13px]">
                {project.client && (
                  <div className="text-white/90"><span className="text-white/60">Client:</span> {project.client}</div>
                )}
                {project.category && (
                  <div className="text-white/85 capitalize"><span className="text-white/60">Type:</span> {project.category.replace('-', ' ')}</div>
                )}
              </div>
              {project.description && (
                <p className="text-gray-300 text-sm mt-3 line-clamp-3">{project.description}</p>
              )}
              <p className="text-[#E06100] text-xs mt-3">Select a video to play</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors shrink-0"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-5">
            {videos.length === 0 ? (
              <div className="text-center text-gray-300 py-10">No videos available.</div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {videos.map((video, idx) => {
                  const supplied = video.duration
                  const derived = computedDurations[idx]
                  const displayDuration = supplied || derived || ''
                  return (
                  <button
                    key={idx}
                    onClick={() => onSelectVideo(video)}
                    className="group flex items-center gap-3 text-left w-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl p-3 sm:p-4 transition-colors"
                  >
                    <div className="w-24 h-14 rounded-lg overflow-hidden bg-black/50 flex items-center justify-center border border-white/10">
                      {(
                        video.thumbnail || computedThumbnails[idx]
                      ) ? (
                        <img
                          src={video.thumbnail || computedThumbnails[idx]}
                          alt={video.title || `Video ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="text-white/70 text-xs">No Thumb</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm sm:text-base font-semibold truncate">{video.title || `Video ${idx + 1}`}</div>
                      {(video.description || video.subtitle) && (
                        <div className="text-gray-400 text-xs sm:text-sm truncate">{video.description || video.subtitle}</div>
                      )}
                    </div>
                    {displayDuration && (
                      <div className="shrink-0 px-2 py-1 rounded-full border border-white/15 text-white/90 text-xs sm:text-sm">
                        {displayDuration}
                      </div>
                    )}
                  </button>
                  )
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}


